import React, { useEffect, useRef } from 'react';
import clsx from 'clsx';
import { AVideoHtml5InternalProps, AVideoAspectRatio } from '../AVideo.types';
import * as Styled from '../AVideo.styles';

export const COMPONENT_NAME = 'a-web-ui-kit-AVideo';
export const TEST_ID = 'web-ui-kit-AVideo-html5';

export const AVideoHtml5: React.FC<AVideoHtml5InternalProps> = ({
  aspectRatio = AVideoAspectRatio.Ratio16x9,
  isPlaying,
  autoPlay = false,
  muted = false,
  loop = false,
  controls = true,
  onAutoplayBlocked,
  onEnded,
  src,
  poster,
  title,
  playsInline = true,
  className,
  ...rest
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  const onAutoplayBlockedRef = useRef(onAutoplayBlocked);
  onAutoplayBlockedRef.current = onAutoplayBlocked;

  const onEndedRef = useRef(onEnded);
  onEndedRef.current = onEnded;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleEnded = () => onEndedRef.current?.();
    video.addEventListener('ended', handleEnded);
    return () => video.removeEventListener('ended', handleEnded);
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- videoRef is stable
  useEffect(() => {
    const video = videoRef.current;
    if (!video || isPlaying === undefined) return;

    if (isPlaying) {
      video.play().catch(() => {
        onAutoplayBlockedRef.current?.();
      });
    } else {
      video.pause();
    }
  }, [isPlaying]);

  if (!src) return null;

  return (
    <Styled.VideoWrapper
      $aspectRatio={aspectRatio}
      data-component-name={COMPONENT_NAME}
      data-testid={TEST_ID}
      className={clsx('a-video', className)}
      {...rest}
    >
      <Styled.VideoElement
        ref={videoRef}
        src={src}
        poster={poster}
        title={title}
        autoPlay={autoPlay}
        muted={muted}
        loop={loop}
        controls={controls}
        playsInline={playsInline}
      />
    </Styled.VideoWrapper>
  );
};
